import React from "react";

import { useBlockGroupContext } from "components/form/Blocks";

import { useSaveActionsContext } from "context/save";

import useSectionGalleryApi from "hooks/api/private/blocks/useSectionGalleryApi";
import useSectionGalleryImageApi from "hooks/api/private/blocks/useSectionGalleryImageApi";
import useOrderableImages, { OrderableImagesHook } from "hooks/useOrderableImages";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import { SectionGalleryBody, SectionGalleryDetails } from "types/api/blocks/sectionGallery";

type SectionGalleryType = {
    buttonDelete: ButtonDeleteInterface;
    orderableImagesHook: OrderableImagesHook;
};

function useSectionGallery(): SectionGalleryType {
    const { saveSectionGallery, getOneSectionGallery, deleteSectionGallery } =
        useSectionGalleryApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const { createSectionGalleryImage, updateSectionGalleryImage, deleteSectionGalleryImage } =
        useSectionGalleryImageApi();

    const orderableImagesHook = useOrderableImages({
        createImage: createSectionGalleryImage,
        updateImage: updateSectionGalleryImage,
        deleteImage: deleteSectionGalleryImage,
        fieldId: "section_gallery_id",
    });

    const {
        formHook: orderableImagesformHook,
        saveOrderableImages,
        initializeOrderableImage,
    } = orderableImagesHook;

    const { showAllError, isValid, form: orderableImagesForm } = orderableImagesformHook;

    const [sectionGalleryDetails, setSectionGalleryDetails] = React.useState<
        SectionGalleryDetails | undefined
    >(undefined);

    React.useEffect(() => {
        if (sectionGalleryDetails) {
            initializeOrderableImage(sectionGalleryDetails.images);
        }
    }, [initializeOrderableImage, sectionGalleryDetails]);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionGallery(
            { id: blockGroupCompositionId },
            { rejectNotFound: true }
        );

        apiCall.then(setSectionGalleryDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionGalleryDetails, getOneSectionGallery, blockGroupCompositionId]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                const body: SectionGalleryBody = {
                    id: blockGroupCompositionId,
                };

                const [apiCall] = saveSectionGallery(body);

                promises.push(apiCall, saveOrderableImages(blockGroupCompositionId));

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveOrderableImages,
        saveSectionGallery,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        orderableImagesForm,
        isValid,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionGalleryType => ({
            buttonDelete: deleteSectionGallery,
            orderableImagesHook,
        }),
        [deleteSectionGallery, orderableImagesHook]
    );
}

export default useSectionGallery;
